import React, { useRef, useEffect, useMemo, useState } from 'react';
import Plotly from 'plotly.js-dist';

interface SingleBetRankData {
  mcRangeTier: string;
  txSignature: string;
  timestamp: string;
  rank: number;
  totalCnt: number;
  value: number;
}

interface SingleBetRankChartProps {
  data: SingleBetRankData[];
  width?: number;
  height?: number;
  title?: string;
  className?: string;
}

const SingleBetRankChart: React.FC<SingleBetRankChartProps> = ({ 
  data, 
  width = 800, 
  height = 400, 
  title = "Single Bet Rank Timeline",
  className = ""
}) => {
  const plotRef = useRef<HTMLDivElement>(null);
  
  // 각 MC Range별 표시/숨김 상태 관리
  const [visibleRanges, setVisibleRanges] = useState<Record<string, boolean>>({
    '0-100k': true,
    '100k-300k': true,
    '300k-1m': true,
    '1m-5m': true,
    '5m+': true
  });

  // MC Range별 색상 정의 (저시총에서 고시총으로 갈수록 진한 빨간색)
  const mcRangeColors = useMemo(() => ({
    '0-100k': '#FCA5A5',    // 연한 빨간색 (가장 연함)
    '100k-300k': '#F87171', // 중간 빨간색
    '300k-1m': '#EF4444',   // 빨간색
    '1m-5m': '#DC2626',     // 진한 빨간색
    '5m+': '#991B1B'        // 매우 진한 빨간색 (가장 진함)
  }), []);

  // Plotly 데이터 생성
  const plotData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // MC Range별로 데이터 그룹화
    const groupedData = data.reduce((acc, item) => {
      if (!acc[item.mcRangeTier]) {
        acc[item.mcRangeTier] = [];
      }
      acc[item.mcRangeTier].push(item);
      return acc;
    }, {} as Record<string, SingleBetRankData[]>);

    // 각 MC Range별로 선 그래프 생성 (visibleRanges 상태에 따라 필터링)
    return Object.entries(groupedData)
      .filter(([mcRange]) => visibleRanges[mcRange]) // 표시되는 MC Range만 필터링
      .map(([mcRange, items]) => {
        // 시간순으로 정렬
        const sortedItems = items.sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        return {
          type: 'scatter',
          mode: 'lines+markers',
          x: sortedItems.map(d => new Date(d.timestamp)),
          y: sortedItems.map(d => d.rank),
          line: {
            color: mcRangeColors[mcRange as keyof typeof mcRangeColors] || '#6B7280',
            width: 2
          },
          marker: {
            size: 6,
            color: mcRangeColors[mcRange as keyof typeof mcRangeColors] || '#6B7280',
            symbol: 'circle'
          },
          name: mcRange,
          hovertemplate: 
            '<b>MC Range</b>: ' + mcRange + '<br>' +
            '<b>시간</b>: %{x}<br>' +
            '<b>Rank</b>: #%{y}<br>' +
            '<b>Value</b>: $%{customdata:,.0f}<br>' +
            '<extra></extra>',
          customdata: sortedItems.map(d => d.value)
        };
      });
  }, [data, visibleRanges, mcRangeColors]);

  // Plotly 레이아웃 설정
  const layout = useMemo(() => ({
    xaxis: {
      title: "시간",
      type: "date",
      gridcolor: '#374151',
      zerolinecolor: '#374151',
      showline: true,
      linecolor: '#6B7280',
      tickfont: {
        color: '#9CA3AF',
        size: 10
      }
    },
    yaxis: {
      title: "Rank (낮을수록 좋음)",
      type: "linear",
      autorange: "reversed", // 낮은 rank가 위로 오도록
      gridcolor: '#374151',
      zerolinecolor: '#374151',
      showline: true,
      linecolor: '#6B7280',
      tickfont: {
        color: '#9CA3AF',
        size: 10
      }
    },
    hovermode: "closest",
    margin: {
      l: 60,
      r: 20,
      b: 50,
      t: 40
    },
    plot_bgcolor: 'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
    font: {
      color: '#FFFFFF'
    },
    title: {
      text: title,
      font: {
        size: 14,
        color: '#FFFFFF'
      },
      x: 0.5,
      y: 0.95
    },
    legend: {
      x: 1,
      y: 1,
      bgcolor: 'rgba(0,0,0,0.8)',
      bordercolor: '#6B7280',
      borderwidth: 1,
      font: {
        color: '#FFFFFF',
        size: 10
      }
    }
  }), [title]);

  // Plotly 설정
  const config = useMemo(() => ({
    displayModeBar: false,
    responsive: true,
    staticPlot: false
  }), []);

  // 차트 렌더링
  useEffect(() => {
    const currentPlotRef = plotRef.current;
    if (!currentPlotRef || !data || data.length === 0) return;

    Plotly.newPlot(currentPlotRef, plotData, layout, config);

    // 컴포넌트 언마운트 시 정리
    return () => {
      if (currentPlotRef) {
        Plotly.purge(currentPlotRef);
      }
    };
  }, [plotData, layout, config, data]);

  // 크기 변경 시 차트 리사이즈
  useEffect(() => {
    if (plotRef.current && data && data.length > 0) {
      Plotly.relayout(plotRef.current, {
        width: width,
        height: height
      });
    }
  }, [width, height, data]);

  // 범례 클릭 핸들러
  const handleLegendClick = (mcRange: string) => {
    setVisibleRanges(prev => ({
      ...prev,
      [mcRange]: !prev[mcRange]
    }));
  };

  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width, height }}>
        <div className="text-gray-400 text-sm">Single Bet Rank 데이터가 없습니다.</div>
      </div>
    );
  }

  return (
    <div className={className} style={{
      marginLeft: -10,
    }}>
      <div 
        ref={plotRef} 
        style={{ width, height }}
        className="w-full h-full"
      />
      
      {/* 범례 */}
      <div className="mb-3 flex flex-wrap justify-center gap-3 text-xs">
        {Object.entries(mcRangeColors).map(([mcRange, color]) => (
          <div 
            key={mcRange} 
            className={`flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity ${
              visibleRanges[mcRange] ? 'opacity-100' : 'opacity-50'
            }`}
            onClick={() => handleLegendClick(mcRange)}
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: color }}
            />
            <span className={`${visibleRanges[mcRange] ? 'text-gray-300' : 'text-gray-500'}`}>
              {mcRange}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SingleBetRankChart;
